/* RESPONSIBLE TEAM: team-phone */

export type Message = {
  content: string;
  end_time: string;
  speaker: string;
  speaker_label: string;
  start_time: string;
};

export interface CallRecordingWireFormat {
  id?: number;
  started_at: string;
  ended_at: string;
  recording_type: 'call' | 'fin_voice';
  url: string | null;
  transcription_url: string | null;
  human_transcript: string | null;
  json_transcript: Message[] | null;
  transcript_status: string | null;
  recording_status: string;
  is_video: boolean;
  has_recording_error: boolean;
}

export interface CallRecording {
  id: string;
  recordingUrl: string | null;
  transcriptUrl: string | null;
  humanTranscript: string | null;
  jsonTranscript: Message[] | null;
  transcriptStatus: string | null;
  recordingName: string | null;
  startedAt: string;
  endedAt: string;
  duration: number;
  recordingType: 'call' | 'fin_voice';
  isVideo: boolean;
  hasRecordingError: boolean;
  recordingStatus: string;
  isDeleted: boolean;
}

export function deserializeCallRecording(json: CallRecordingWireFormat): CallRecording | null {
  // Calculate duration in seconds from timestamps
  let startTime = new Date(json.started_at).getTime();
  let endTime = new Date(json.ended_at).getTime();

  // Validate timestamps - return null instead of throwing to allow graceful degradation
  if (isNaN(startTime) || isNaN(endTime)) {
    console.error(
      `Skipping invalid recording: Invalid timestamp in recording: started_at=${json.started_at}, ended_at=${json.ended_at}, url=${json.url}`,
    );
    return null;
  }

  // Calculate duration, ensuring it's non-negative
  let duration = Math.floor((endTime - startTime) / 1000);
  if (duration < 0) {
    // If end time is before start time, log warning and use 0 duration
    // This handles edge cases where data might be corrupted
    console.warn(
      `Recording has negative duration (ended_at < started_at): ${json.url}. Using duration of 0.`,
    );
    duration = 0;
  }

  // Use the ID from the API response if available, otherwise extract from URL, or fallback to timestamp-based ID
  let id =
    json.id?.toString() ||
    json.url?.match(/\/recordings\/(\d+)\//)?.[1] ||
    `${json.recording_type}-${startTime}`;

  let isDeleted = json.recording_status === 'deleted';
  // When recording is deleted, URL should be null to prevent player from trying to load it
  let recordingUrl = isDeleted ? null : json.url || null;

  return {
    id,
    recordingUrl,
    transcriptUrl: json.transcription_url,
    humanTranscript: json.human_transcript,
    jsonTranscript: json.json_transcript,
    transcriptStatus: json.transcript_status,
    recordingName: null, // recording_name not provided in new API format
    startedAt: json.started_at,
    endedAt: json.ended_at,
    duration,
    recordingType: json.recording_type,
    isVideo: json.is_video,
    hasRecordingError: json.has_recording_error,
    recordingStatus: json.recording_status || 'available',
    isDeleted,
  };
}

/**
 * Creates a recording from legacy API fields.
 * Used for backwards compatibility with older API responses.
 */
export function createLegacyRecording(
  id: string,
  type: 'call' | 'fin_voice',
  url: string,
  transcriptUrl: string | null,
  humanTranscript: string | null,
  jsonTranscript: Message[] | null,
  transcriptStatus: string | null,
  recordingName: string | null,
  startedAt: string,
  endedAt: string,
  duration: number,
): CallRecording {
  let isDeleted = recordingName === 'deleted';
  return {
    id,
    recordingUrl: url,
    transcriptUrl,
    humanTranscript,
    jsonTranscript,
    transcriptStatus,
    recordingName,
    startedAt,
    endedAt,
    duration,
    recordingType: type,
    isVideo: false, // Legacy recordings default to audio
    hasRecordingError: false,
    recordingStatus: isDeleted ? 'deleted' : 'available',
    isDeleted,
  };
}

/**
 * Serializes a recording to wire format.
 */
export function serializeCallRecording(recording: CallRecording): CallRecordingWireFormat {
  return {
    started_at: recording.startedAt,
    ended_at: recording.endedAt,
    recording_type: recording.recordingType,
    url: recording.recordingUrl,
    transcription_url: recording.transcriptUrl,
    human_transcript: recording.humanTranscript,
    json_transcript: recording.jsonTranscript,
    transcript_status: recording.transcriptStatus,
    recording_status: recording.recordingStatus,
    is_video: recording.isVideo,
    has_recording_error: recording.hasRecordingError,
  };
}
